import { type Sensor, sensorSchema } from '@si/types';
import { get, objectToQueryParams } from '@si/utils';
import { useQuery } from '@tanstack/react-query';
import { z } from 'zod';

const responseSchema = z.object({
  sensors: z.array(sensorSchema),
});

interface Params {
  projectId: string;
  from?: string;
  to?: string;
  includeReplaced?: boolean;
  enabled?: boolean;
  noRetry?: boolean;
}

export function useSensorsWithHighestStatus({
  projectId,
  from,
  to,
  includeReplaced = false,
  enabled = true,
  noRetry = false,
}: Params) {
  const queryKey = ['highestStatusPerSensor', { projectId, from, to, includeReplaced }];
  const url = `/projects/${projectId}/sensors${objectToQueryParams({
    includeHighestSensorStatus: true,
    includeReplaced,
    from,
    to,
  })}`;

  const { data, error, isLoading, refetch, isFetching } = useQuery<{
    sensors: Sensor[];
  }>({
    queryKey,
    queryFn: () => get({ url, schema: responseSchema }),
    enabled: enabled && !!projectId && !!from && !!to,
    retry: !noRetry,
  });

  return {
    sensors: data?.sensors || [],
    error,
    isLoading,
    isFetching,
    refetch,
  };
}
